import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AssessmentPresetService, EquipmentLinkedCheckpointDto, EquipmentService, } from '@artemis-software/wr-api';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { firstValueFrom } from 'rxjs';
import { ConfirmDialogComponent } from '@components/dialog/confirm-dialog/confirm-dialog.component';

@Component({
  standalone: false,
  selector: 'wr-tga-type-linked-checkpoints',
  templateUrl: './tga-type-linked-checkpoints.component.html',
  styleUrls: ['./tga-type-linked-checkpoints.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TgaTypeLinkedCheckpointsComponent {
  private readonly equipmentService = inject(EquipmentService);
  private readonly assessmentPresetService = inject(AssessmentPresetService);
  private readonly router = inject(Router);
  private readonly dialogService = inject(NbDialogService);
  private readonly toastrService = inject(NbToastrService);

  readonly equipmentId = input.required<string>();
  readonly isEditing = input.required<boolean>();

  readonly filterText = signal('');
  readonly expandedCheckpointIds = signal<Set<string>>(new Set());
  readonly linkedCheckpoints = signal<EquipmentLinkedCheckpointDto[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly selectedPresetIds = signal<Set<string>>(new Set());

  readonly filteredCheckpoints = computed<EquipmentLinkedCheckpointDto[]>(() => {
    const text = this.filterText().trim().toLowerCase();
    const items = this.linkedCheckpoints();
    if (!text) {
      return items;
    }
    return items.filter((link) =>
      link.checkpoint.name.toLowerCase().includes(text) ||
      `${link.checkpoint.checkpointGroupNumber}.${link.checkpoint.number}`.includes(text) ||
      link.assessmentPresets.some((preset) => preset.text.toLowerCase().includes(text)),
    );
  });

  readonly count = computed(() => this.linkedCheckpoints().length);

  readonly allExpanded = computed(() => {
    const visible = this.filteredCheckpoints();
    if (visible.length === 0) {
      return false;
    }
    const expanded = this.expandedCheckpointIds();
    return visible.every((link) => expanded.has(link.checkpoint.id));
  });

  constructor() {
    effect(() => {
      const id = this.equipmentId();
      if (id) {
        void this.reload(id);
      }
    });
    effect(() => {
      if (!this.isEditing()) {
        this.selectedPresetIds.set(new Set());
      }
    });
  }

  isExpanded(checkpointId: string): boolean {
    return this.expandedCheckpointIds().has(checkpointId);
  }

  onCollapsedChange(checkpointId: string, isCollapsed: boolean): void {
    const next = new Set(this.expandedCheckpointIds());
    if (isCollapsed) {
      next.delete(checkpointId);
    } else {
      next.add(checkpointId);
    }
    this.expandedCheckpointIds.set(next);
  }

  toggleExpandAll(): void {
    if (this.allExpanded()) {
      this.expandedCheckpointIds.set(new Set());
    } else {
      this.expandedCheckpointIds.set(new Set(this.filteredCheckpoints().map((link) => link.checkpoint.id)));
    }
  }

  navigateToCheckpoint(checkpointId: string): void {
    void this.router.navigate(['checkpoint', checkpointId]);
  }

  isPresetSelected(presetId: string): boolean {
    return this.selectedPresetIds().has(presetId);
  }

  togglePresetSelection(presetId: string): void {
    const next = new Set(this.selectedPresetIds());
    if (next.has(presetId)) {
      next.delete(presetId);
    } else {
      next.add(presetId);
    }
    this.selectedPresetIds.set(next);
  }

  hasSelectionInCheckpoint(link: EquipmentLinkedCheckpointDto): boolean {
    const selected = this.selectedPresetIds();
    return link.assessmentPresets.some((preset) => selected.has(preset.id));
  }

  isAllSelectedInCheckpoint(link: EquipmentLinkedCheckpointDto): boolean {
    if (link.assessmentPresets.length === 0) return false;
    const selected = this.selectedPresetIds();
    return link.assessmentPresets.every((preset) => selected.has(preset.id));
  }

  isPartiallySelectedInCheckpoint(link: EquipmentLinkedCheckpointDto): boolean {
    return this.hasSelectionInCheckpoint(link) && !this.isAllSelectedInCheckpoint(link);
  }

  toggleAllInCheckpoint(link: EquipmentLinkedCheckpointDto): void {
    const next = new Set(this.selectedPresetIds());
    if (this.isAllSelectedInCheckpoint(link)) {
      link.assessmentPresets.forEach((preset) => next.delete(preset.id));
    } else {
      link.assessmentPresets.forEach((preset) => next.add(preset.id));
    }
    this.selectedPresetIds.set(next);
  }

  async unlinkSelected(link: EquipmentLinkedCheckpointDto): Promise<void> {
    if (!this.isEditing()) return;
    const selected = this.selectedPresetIds();
    const presetIds = link.assessmentPresets
      .filter((preset) => selected.has(preset.id))
      .map((preset) => preset.id);
    if (presetIds.length === 0) return;

    const confirmed = await firstValueFrom(
      this.dialogService.open(ConfirmDialogComponent, {
        context: {
          header: 'Verknüpfung aufheben',
          message: presetIds.length === 1
            ? 'Soll die Verknüpfung zwischen dieser Feststellung und dem TGA Bauplan wirklich aufgehoben werden?'
            : `Sollen die ${presetIds.length} Verknüpfungen wirklich aufgehoben werden?`,
          status: 'danger',
          submitText: 'Aufheben',
          cancelText: 'Abbrechen',
        },
      }).onClose,
    );
    if (!confirmed) return;

    const results = await Promise.allSettled(
      presetIds.map((presetId) => firstValueFrom(this.assessmentPresetService.unlinkEquipment(presetId))),
    );
    const fulfilledIds = presetIds.filter((_, i) => results[i].status === 'fulfilled');
    const failedCount = results.length - fulfilledIds.length;

    const next = new Set(this.selectedPresetIds());
    fulfilledIds.forEach((id) => next.delete(id));
    this.selectedPresetIds.set(next);

    if (failedCount === 0) {
      this.toastrService.success(
        presetIds.length === 1 ? 'Verknüpfung aufgehoben' : `${presetIds.length} Verknüpfungen aufgehoben`,
        'Erfolg',
      );
    } else if (fulfilledIds.length === 0) {
      console.error('Failed to unlink assessment preset(s)', results);
      this.toastrService.danger('Verknüpfung konnte nicht aufgehoben werden.', 'Fehler');
    } else {
      console.error('Some assessment presets failed to unlink', results);
      this.toastrService.warning(
        `${fulfilledIds.length} von ${presetIds.length} Verknüpfungen aufgehoben. ${failedCount} fehlgeschlagen.`,
        'Teilweise erfolgreich',
      );
    }

    await this.reload(this.equipmentId());
  }

  async reload(equipmentId: string): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    try {
      const items = await firstValueFrom(this.equipmentService.findLinkedCheckpointsByEquipmentId(equipmentId));
      this.linkedCheckpoints.set(items ?? []);
    } catch (e) {
      console.error('Failed to load linked checkpoints', e);
      this.error.set('Verknüpfungen konnten nicht geladen werden.');
      this.linkedCheckpoints.set([]);
    } finally {
      this.loading.set(false);
    }
  }
}
